import { ThemeMode } from 'antd-style';
import isEqual from 'fast-deep-equal';
import { gt, parse, valid } from 'semver';
import { SWRResponse } from 'swr';
import type { StateCreator } from 'zustand/vanilla';

import { SILVERTIGER_THEME_APPEARANCE } from '@/const/theme';
import { CURRENT_VERSION } from '@/const/version';
import { useOnlyFetchOnceSWR } from '@/libs/swr';
import { globalService } from '@/services/global';
import type { WSContext } from '@/libs/embed-brige/embed-bridge';
import type { EmbedParams, SystemStatus } from '@/store/global/initial-state';
import { LocaleMode } from '@/types_cp/locale';
import { setCookie } from '@/utils_cp/client/cookie';
import { switchLangNextIntl } from '@/utils_cp/client/switch-lang-next-intl';
import { merge } from '@/utils_cp/merge';
import { setNamespace } from '@/utils_cp/store-debug';

import type { GlobalStore } from '../store';

const n = setNamespace('g');

export interface GlobalGeneralAction {
  setEmbeddedAppUrl: (url: string | null) => void;
  setWorkspaceContext: (ctx: WSContext | null) => void;
  setEmbedParams: (params: EmbedParams) => void;
  switchLocale: (locale: LocaleMode) => void;
  switchThemeMode: (themeMode: ThemeMode, params?: { skipBroadcast?: boolean }) => void;
  updateSystemStatus: (status: Partial<SystemStatus>, action?: any) => void;
  useCheckLatestVersion: (enabledCheck?: boolean) => SWRResponse<string>;
  useInitSystemStatus: () => SWRResponse;
}

export const generalActionSlice: StateCreator<
  GlobalStore,
  [['zustand/devtools', never]],
  [],
  GlobalGeneralAction
> = (set, get) => ({
  setEmbeddedAppUrl: url => set({ embeddedAppUrl: url }, false, n('setEmbeddedAppUrl')),
  setWorkspaceContext: ctx => set({ workspaceContext: ctx }, false, n('setWorkspaceContext')),
  setEmbedParams: params => {
    set({ embedParams: params }, false, n('setEmbedParams'));
  },
  switchLocale: locale => {
    get().updateSystemStatus({ language: locale });
    switchLangNextIntl(locale);
  },
  switchThemeMode: (themeMode, { skipBroadcast: _skipBroadcast } = {}) => {
    get().updateSystemStatus({ themeMode });

    setCookie(SILVERTIGER_THEME_APPEARANCE, themeMode === 'auto' ? undefined : themeMode);
  },
  updateSystemStatus: (status, action) => {
    const nextStatus = merge(get().status, status);
    if (isEqual(get().status, nextStatus)) return;
    set({ status: nextStatus }, false, action || n('updateSystemStatus'));
    get().statusStorage.saveToLocalStorage(nextStatus);
  },

  useCheckLatestVersion: (enabledCheck = true) =>
    useOnlyFetchOnceSWR(
      enabledCheck ? 'checkLatestVersion' : null,
      async () => globalService.getLatestVersion(),
      {
        focusThrottleInterval: 1000 * 60 * 30,
        onSuccess: (data: string) => {
          if (!valid(CURRENT_VERSION) || !valid(data)) return;

          const currentVersion = parse(CURRENT_VERSION);
          const latestVersion = parse(data);

          if (!currentVersion || !latestVersion) return;

          const currentMajorMinor = `${currentVersion.major}.${currentVersion.minor}.0`;
          const latestMajorMinor = `${latestVersion.major}.${latestVersion.minor}.0`;

          if (gt(latestMajorMinor, currentMajorMinor)) {
            set({ hasNewVersion: true, latestVersion: data }, false, n('checkLatestVersion'));
          }
        },
      }
    ),

  useInitSystemStatus: () =>
    useOnlyFetchOnceSWR<SystemStatus>(
      'initSystemStatus',
      () => get().statusStorage.getFromLocalStorage(),
      {
        onSuccess: status => {
          set({ isStatusInit: true }, false, 'setStatusInit');

          get().updateSystemStatus(status, 'initSystemStatus');
        },
      }
    ),
});
